import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { BookingDto, tableRowsAtomFamily } from "@ryde/shared";

import { useStarredRows } from "../dynamic-tables";
import { useHighlightEdited } from "../use-highlight-edited";

export const useBookingsListActions = () => {
  const { setHighlightedRows } = useHighlightEdited("booking");
  const { removeStarredIds } = useStarredRows("booking");
  const [bookings, setBookings] = useRecoilState(
    tableRowsAtomFamily("booking"),
  );

  const addBooking = useEvent((booking: BookingDto): void => {
    setBookings([...(bookings || []), { ...booking }]);

    if (booking.id) {
      setHighlightedRows([booking.id]);
    }
  });

  const replaceBooking = useEvent((bookingUpdated: BookingDto): void => {
    const updatedBookings = bookings?.map(booking => {
      return booking.id === bookingUpdated.id ? bookingUpdated : booking;
    });
    setBookings(updatedBookings);

    if (bookingUpdated?.id) {
      setHighlightedRows([bookingUpdated.id]);
    }
  });

  const margeBookingRow = useEvent((bookingUpdated: BookingDto): void => {
    const updatedBookings = bookings?.map(booking => {
      return booking.id === bookingUpdated.id
        ? { ...booking, ...bookingUpdated }
        : booking;
    });
    setBookings(updatedBookings);

    if (bookingUpdated?.id) {
      setHighlightedRows([bookingUpdated.id]);
    }
  });

  const replaceBookings = useEvent((bookingsUpdated: BookingDto[]): void => {
    const bookingsUpdatedMap = new Map(bookingsUpdated.map(e => [e.id, e]));
    const updatedBookings = bookings?.map(booking => {
      const updatedBooking = bookingsUpdatedMap.get(booking.id);

      return updatedBooking ? updatedBooking : booking;
    });
    setBookings(updatedBookings);

    if (updatedBookings?.length) {
      setHighlightedRows(updatedBookings.map(x => x.id));
    }
  });

  const removeBooking = useEvent((bookingId: number): void => {
    const updatedBookings = bookings?.filter(
      booking => booking.id !== bookingId,
    );
    setBookings(updatedBookings);
    removeStarredIds([bookingId]);
  });

  const removeBookings = useEvent((bookingIds: number[]): void => {
    const bookingIdsSet = new Set(bookingIds);
    const updatedBookings = bookings?.filter(
      booking => !bookingIdsSet.has(booking.id),
    );
    setBookings(updatedBookings);
    removeStarredIds(bookingIds);
  });

  return {
    addBooking,
    replaceBooking,
    margeBookingRow,
    replaceBookings,
    removeBooking,
    removeBookings,
  };
};
